import { CaretDownIcon, CaretUpIcon, CheckIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform, Pressable, StyleProp, StyleSheet, Text, View, ViewStyle } from "react-native";
import { Portal } from "react-native-paper";

interface OptionItem {
  label: string;
  value: string;
}

interface CustomDropdownWebProps {
  options: OptionItem[];
  value: string | undefined;
  onChange: (value: string) => void;
  style?: StyleProp<ViewStyle>;
  error?: string;
  openUpward?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  placeholderTextColor?: string;
  accessibilityLabel?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  dropdownStyle?: StyleProp<ViewStyle>;
  dropdownContainerStyle?: StyleProp<ViewStyle>;
  showArrow?: boolean;
  hideItemBorders?: boolean;
}

interface DropdownMenuPosition {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
}

export const CustomDropdownWeb = ({
  value,
  options,
  onChange,
  style,
  error,
  openUpward = false,
  disabled = false,
  fullWidth = true,
  placeholder = "Select an Option",
  placeholderTextColor,
  accessibilityLabel,
  open: controlledOpen,
  onOpenChange,
  dropdownStyle,
  dropdownContainerStyle,
  showArrow = true,
  hideItemBorders = false,
}: CustomDropdownWebProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(controlledOpen ?? false);
  const [isActive, setIsActive] = useState(false);
  const [hoveredOption, setHoveredOption] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<DropdownMenuPosition | null>(null);
  const dropdownRef = useRef(null);
  const isWeb = Platform.OS === "web";

  useEffect(() => {
    if (controlledOpen != null) {
      setIsOpen(controlledOpen);
    }
  }, [controlledOpen]);

  const openState = isOpen;

  const setOpenState = useCallback(
    (next: boolean) => {
      setIsOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange]
  );

  const closeDropdown = useCallback(() => {
    setOpenState(false);
    setIsActive(false);
  }, [setOpenState]);

  // RN style types don't support overflowY/overflowX, but web does.
  // Casting through unknown keeps us type-safe without introducing `any`.
  const optionListWebOverflowStyle = isWeb
    ? ({ overflowY: "auto", overflowX: "hidden" } as unknown as ViewStyle)
    : null;

  const combinedStyles = [
    styles.dropdownContainer,
    fullWidth ? styles.fullWidth : styles.fitContent,
    error && error.length > 0 ? styles.dropdownContainerWithErrorSpace : null,
    openState ? styles.dropdownContainerOpen : null,
    style,
  ];

  const selectedOption = useMemo(() => options.find((option) => option.value === value), [options, value]);

  const updateMenuPosition = useCallback(() => {
    if (!isWeb || !openState || typeof window === "undefined") {
      return;
    }

    const dropdownElement = dropdownRef.current as HTMLElement | null;
    if (!dropdownElement) {
      return;
    }

    const rect = dropdownElement.getBoundingClientRect();

    setMenuPosition(
      openUpward
        ? {
            left: rect.left,
            width: rect.width,
            bottom: window.innerHeight - rect.top + 4,
          }
        : {
            left: rect.left,
            width: rect.width,
            top: rect.bottom + 4,
          }
    );
  }, [isWeb, openState, openUpward]);

  const handleSelect = useCallback(
    (option: string) => {
      onChange(option);
      setOpenState(false);
      setIsActive(true);
    },
    [onChange, setOpenState]
  );

  useEffect(() => {
    if (!isWeb || !openState || typeof window === "undefined" || typeof document === "undefined") {
      setMenuPosition(null);
      return;
    }

    updateMenuPosition();

    const handleViewportChange = () => {
      updateMenuPosition();
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeDropdown();
      }
    };

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    document.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [closeDropdown, isWeb, openState, updateMenuPosition]);

  const currentLabel = selectedOption ? selectedOption.label : placeholder;

  const buttonStyle = [
    styles.selectedValue,
    fullWidth ? styles.fullWidth : styles.fitContent,
    !fullWidth && styles.fillContainer,
    ...(disabled ? [styles.buttonDisabled] : []),
    ...(isActive ? (error != null && error !== "" ? [styles.buttonError] : [styles.buttonActive]) : []),
    dropdownStyle,
  ];

  const textStyle = selectedOption
    ? styles.textLabel
    : placeholderTextColor
      ? [styles.textPlaceholder, { color: placeholderTextColor }]
      : styles.textPlaceholder;

  const optionListContent = (
    <View
      style={[styles.optionList, styles.optionListPortal, optionListWebOverflowStyle, dropdownContainerStyle]}
      role="listbox">
      {options.map((option) => {
        const isSelected = value === option.value;
        const isHovered = hoveredOption === option.value;

        return (
          <Pressable
            accessibilityLabel={option.label}
            key={option.value}
            role="option"
            aria-selected={isSelected}
            onPress={() => {
              handleSelect(option.value);
            }}
            onHoverIn={() => setHoveredOption(option.value)}
            onHoverOut={() => setHoveredOption(null)}
            style={[
              styles.optionItem,
              hideItemBorders ? styles.optionItemNoBorder : null,
              isHovered ? styles.optionItemHover : null,
            ]}>
            {isSelected && (
              <View style={styles.checkIcon}>
                <CheckIcon size={16} style={{ height: 16, width: 16, color: "inherit" }} />
              </View>
            )}
            <Text style={styles.textLabel}>{option.label}</Text>
          </Pressable>
        );
      })}
    </View>
  );

  const portalWrapperStyle = menuPosition
    ? ({
        position: "fixed",
        left: menuPosition.left,
        width: menuPosition.width,
        zIndex: 10000,
        ...(menuPosition.top != null ? { top: menuPosition.top } : {}),
        ...(menuPosition.bottom != null ? { bottom: menuPosition.bottom } : {}),
      } as unknown as ViewStyle)
    : null;

  const portalBackdropStyle = isWeb
    ? ({
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: "transparent",
        zIndex: 9999,
      } as unknown as ViewStyle)
    : null;

  return (
    <>
      <View style={combinedStyles} ref={dropdownRef}>
        <Pressable
          style={buttonStyle}
          onPress={() => {
            if (disabled) {
              return;
            }

            setOpenState(!openState);
          }}
          disabled={disabled}
          accessibilityLabel={accessibilityLabel || placeholder}
          accessibilityRole="combobox"
          aria-expanded={openState}
          aria-haspopup="listbox"
          aria-label={accessibilityLabel || placeholder}>
          <Text style={textStyle}>{currentLabel}</Text>
          {showArrow &&
            (openState ? (
              <CaretUpIcon style={styles.caretIcon} size={16} />
            ) : (
              <CaretDownIcon style={styles.caretIcon} size={16} />
            ))}
        </Pressable>

        <View
          style={styles.ghostContainer}
          accessibilityElementsHidden={true}
          importantForAccessibility="no-hide-descendants">
          <View style={styles.selectedValue}>
            <Text style={styles.textLabel}>{placeholder}</Text>
            <CaretDownIcon style={styles.caretIcon} size={16} />
          </View>
          {options.map((option) => (
            <View key={option.value} style={styles.selectedValue}>
              <Text style={styles.textLabel}>{option.label}</Text>
              <CaretDownIcon style={styles.caretIcon} size={16} />
            </View>
          ))}
        </View>

        {error && error.length > 0 ? <Text style={styles.errorText}>{error}</Text> : null}
      </View>

      {openState && menuPosition ? (
        <Portal>
          <Pressable
            style={portalBackdropStyle}
            onPress={closeDropdown}
            accessibilityLabel={`Close ${accessibilityLabel || placeholder}`}
            accessibilityRole="button"
          />
          <View style={portalWrapperStyle}>{optionListContent}</View>
        </Portal>
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  dropdownContainer: {
    position: "relative",
    zIndex: 0,
    elevation: 0,
  },
  fullWidth: {
    width: "100%",
  },
  fitContent: {
    alignSelf: "flex-start",
  },
  fillContainer: {
    width: "100%",
  },
  dropdownContainerWithErrorSpace: {
    marginBottom: 18,
  },
  dropdownContainerOpen: {
    zIndex: 9999,
    elevation: 9999,
  },
  selectedValue: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 12,
    backgroundColor: "white",
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    minHeight: 40,
  },
  buttonActive: {
    borderColor: "#4f46e5",
  },
  buttonDisabled: {
    backgroundColor: "#f3f4f6",
    borderColor: "#d1d5db",
  },
  buttonError: {
    borderColor: "#ef4444",
  },
  optionList: {
    padding: 4,
    backgroundColor: "white",
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    maxHeight: 240,
    overflow: "hidden",
  },
  optionListPortal: {
    width: "100%",
  },
  optionItem: {
    maxHeight: 400,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingTop: 10,
    paddingBottom: 10,
    paddingRight: 16,
    paddingLeft: 30,
    borderRadius: 6,
    position: "relative",
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
    width: "100%",
  },
  optionItemHover: {
    backgroundColor: "#f0f9ff",
  },
  optionItemNoBorder: {
    borderBottomWidth: 0,
  },
  checkIcon: {
    position: "absolute",
    left: 6,
    top: "50%",
    marginTop: -8,
    height: 16,
    width: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  errorText: {
    position: "absolute",
    top: "100%",
    right: 0,
    left: 0,
    color: "#ef4444",
    fontSize: 12,
    marginTop: 4,
    marginLeft: 8,
  },
  caretIcon: {
    height: 16,
    width: 16,
    color: "#6b7280",
  },
  textLabel: {
    color: "#1f2937",
    fontWeight: "400",
    fontSize: 14,
  },
  textPlaceholder: {
    color: "#9ca3af",
    fontSize: 14,
  },
  ghostContainer: {
    height: 0,
    overflow: "hidden",
    opacity: 0,
  },
});
